import React, { memo, useCallback } from 'react';
import {
  IconSuccessCircle,
  IconWarningCircle,
  IconWarningTriangle,
} from '@app/icons';
import { Alert } from 'components/ui/alert';
import { cn } from 'lib/utils';

export type NotificationVariants = 'success' | 'error' | 'warning' | 'info';

export interface Props {
  message: string;
  variant: NotificationVariants;
  className?: string;
}

function Notification(props: Props) {
  const { message, className = '', variant = 'info' } = props;

  const renderIcon = useCallback(() => {
    switch (variant) {
      case 'success':
        return <IconSuccessCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <IconWarningTriangle className="h-5 w-5 text-red-600" />;
      case 'warning':
        return <IconWarningCircle className="h-5 w-5 text-yellow-600" />;
      default:
        return <IconWarningCircle className="h-5 w-5 text-blue-600" />;
    }
  }, [variant]);

  const alertVariant = variant === 'error' ? 'destructive' : 'default';
  const alertClass =
    variant === 'success' ? 'bg-green-500 text-white border-none' : '';

  return (
    <Alert
      variant={alertVariant}
      className={cn('flex items-start gap-3 p-4', alertClass, className)}
    >
      <div className="flex-shrink-0 mt-0.5">{renderIcon()}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-5">{message}</p>
      </div>
    </Alert>
  );
}

export default memo(Notification);
